import {
    Component, ViewChild, ComponentRef,
    ViewContainerRef, ComponentFactory,
    ComponentFactoryResolver, HostBinding, OnInit, OnDestroy
} from '@angular/core';
import {Location} from '@angular/common';
import {SnackbarService} from 'app/core';
import {ActivatedRoute, Router} from '@angular/router';
import {FormControl, Validators} from '@angular/forms';

import {ProductSale, SetPriceModel, ProductDescriptionModel, ProductStorePricesListQuery, ProductLogicOptionsEnum} from '../model/model';
import {ProductsDataService} from '../services/products.data.service';
import {GeneralService, PriceSensPolishEnum} from 'app/core';
import {
    ProductLogicsDefaultComponent,
    ProductLogicsDependentComponent,
    ProductLogicsMainComponent
} from './product-logics/product-logics.component';
import {Subscription} from 'rxjs';
import {TableColumnDef} from 'app/shared/tables/table-column-def';
import {
    AttributeWithId,
    FeaturesDataService,
    FeatureSetCommand,
    FeatureUpdateCommand
} from 'app/domain/features/services/features.data.service';

export enum FeatureTypes {
    Select = 'Select',
    Text = 'Text',
    Boolean = 'Boolean',
}

@Component({
    selector: 'sh-app-product-page',
    templateUrl: './product-page.component.html',
    styleUrls: ['./product-page.component.scss']
})

export class ProductPageComponent implements OnInit, OnDestroy {

    @HostBinding('class') classes = 'flex flex-grow action-button-padding';

    @ViewChild('productLogics', {read: ViewContainerRef, static: true}) container;
    componentRef: ComponentRef<any>;

    product: ProductDescriptionModel = {} as ProductDescriptionModel;
    isSubmittingForm = false;
    errors: Object = {};
    prices: ProductSale;
    maxPrice: string = null;
    defaultMaxPrice: string = null;
    minMargin: number;
    maxMargin: number;
    useMinMargin: boolean;
    useMaxMargin: boolean;

    changedAttributes: AttributeWithId[];
    initialAttributes: AttributeWithId[];
    allFeatures: FeatureUpdateCommand[];

    categoryList: Array<string> = [];

    priceEnum = PriceSensPolishEnum;

    inputFocus1 = false;
    inputFocus2 = false;
    inputFocus3 = false;

    sub: Subscription = null;

    canNavigate = false;
    columnsData: TableColumnDef[];
    tableId: string;
    dataServiceMethod = 'fetchInnerData';

    areFeaturesLoading = false;
    isFeatureLoading = false;
    isFeaturesUpdating = false;

    FeatureTypes = FeatureTypes;

    navigationBackArray = ['metadata', 'articles'];

    constructor(
        public dataService: ProductsDataService,
        public featureService: FeaturesDataService,
        public snackbar: SnackbarService,
        private route: ActivatedRoute,
        private router: Router,
        private _location: Location,
        public generalService: GeneralService,
        private resolver: ComponentFactoryResolver
    ) {
    }

    ngOnInit() {
        this.areFeaturesLoading = true;
        this.isFeatureLoading = true;
        // setting prices that can be changed
        this.featureService.fetchData(100, 0, '', '', [])
          .subscribe(
            res => {
                this.allFeatures = res.items;
                this.areFeaturesLoading = false;
            });
        this.sub = this.route.params
            .subscribe(() => {
                this.product = this.route.snapshot.data['product'] ? this.route.snapshot.data['product'] : this.router.navigateByUrl('/');
                this.prices = this.product.Sale;
                // create logics component
                this.createLogicsComponent();

                this.dataService.productId = this.product.Id;
                const tableInfo = ProductStorePricesListQuery.tableInfo();
                this.columnsData = tableInfo.columns;
                this.tableId = tableInfo.tableId;

                this.getProductFeatures();
            });
    }


    ngOnDestroy() {
        this.componentRef.destroy();
    }

    private getProductFeatures() {
        this.featureService.getFeatureById(this.product.Id)
            .subscribe(
                res => {
                    this.initialAttributes = res.AttributesList.map((attribute, index) => ({
                        AttributeId: attribute.AttributeId,
                        Value: attribute.Value,
                        Id: index,
                    }));
                    this.changedAttributes = JSON.parse(JSON.stringify(this.initialAttributes));
                    this.isFeatureLoading = false;
                });
    }

    public getFeatureType(featureId: number): string | undefined {
        return this.allFeatures &&
            this.allFeatures.find(feature => feature.id === featureId).type;
    }

    public getPossibleValues(featureId: number): string[] | undefined {
        return this.allFeatures &&
            this.allFeatures.find(feature => feature.id === featureId) &&
            this.allFeatures.find(feature => feature.id === featureId).possibleValues;
    }

    createLogicsComponent() {
        let type;
        // define product type
        if (this.product.IsProductDependent) {
            type = ProductLogicOptionsEnum.dependent;
        } else if (this.product.IsProductMain) {
            type = ProductLogicOptionsEnum.main;
        } else {
            type = ProductLogicOptionsEnum.default;
        }
        // create new component depending on type
        if (type === ProductLogicOptionsEnum.main) {
            this.createLogicMain();
        } else if (type === ProductLogicOptionsEnum.dependent) {
            this.createLogicDependent();
        } else {
            this.createLogicDefault();
        }
    }

    private createLogicDefault() {
        this.container.clear();
        const factory: ComponentFactory<any> = this.resolver.resolveComponentFactory(ProductLogicsDefaultComponent);
        this.componentRef = this.container.createComponent(factory);
        // fill input()/output()
        this.componentRef.instance.productId = this.product.Id;
        this.componentRef.instance.changeType
            .subscribe(
                this.changeType.bind(this)
            );
    }

    private createLogicDependent() {
        this.container.clear();
        const factory: ComponentFactory<any> = this.resolver.resolveComponentFactory(ProductLogicsDependentComponent);
        this.componentRef = this.container.createComponent(factory);
        // fill input()/output()
        this.componentRef.instance.productId = this.product.Id;
    }

    private createLogicMain() {
        this.container.clear();
        const factory: ComponentFactory<any> = this.resolver.resolveComponentFactory(ProductLogicsMainComponent);
        this.componentRef = this.container.createComponent(factory);
        // fill input()/output()
        this.componentRef.instance.product = this.product;
        this.componentRef.instance.changeType
            .subscribe(
                this.changeType.bind(this),
            );
    }

    private changeType(newType: ProductLogicOptionsEnum): any {
        this.product.IsProductMain = !!newType;
        this.createLogicsComponent();
    }

    private constructNewString(price: string): string {
        const separator = price.includes(',') ? ',' : '.';
        const newPrice = price.split(separator)[0];

        if (price.split(separator).length === 1) {
            return newPrice.concat(separator, '00');
        } else {
            const float = price.split(separator)[1].concat('00');
            return newPrice.concat(separator, float.slice(0, 2));
        }
    }

    parseDate(dateStr: string) {
        return dateStr ? new Date(dateStr) : null;
    }

    formPricesData(maxPrc, minMarg, maxMarg, useMinMargin, useMaxMargin) {
        return new SetPriceModel(this.product.Id, maxPrc, minMarg, maxMarg, useMinMargin, useMaxMargin);
    }

    public editFeatures() {
        this.isFeaturesUpdating = true;
        // check for duplicates
        if (this.changedAttributes.some(feature => this.isFeatureDuplicated(feature.AttributeId))) {
            this.snackbar.info('Wybrane cechy nie mogą się powtarzać');
            this.isFeaturesUpdating = false;
            return;
        }
        // check for empty values
        if (this.changedAttributes.find(feature => feature.Value === '')) {
            this.snackbar.info('Uzupełnij wartości cech');
            this.isFeaturesUpdating = false;
            return;
        }

        const data = this.formEditFeatureData();

        this.featureService.setFeatures(data)
          .subscribe(
            () => {
                this.snackbar.info('Udało się zapisać cechy produktu.');
                this.isFeaturesUpdating = false;
            },
            (err) => {
                if (err.status !== 403) {
                    this.snackbar.info('Wystąpił błąd podczas zapisywania danych.');
                    this.isFeaturesUpdating = false;
                }
            });
    }

    private formEditFeatureData = (): FeatureSetCommand => {
        return {
            productAttributesList: [
                {
                  productId: this.product.Id,
                  attributesList: this.changedAttributes.map((feature) => ({
                      attributeId: feature.AttributeId,
                      value: feature.Value,
                  })),
                }
            ]
        };
    }

    public cancelFeatures() {
        this.changedAttributes = JSON.parse(JSON.stringify(this.initialAttributes));
    }


    // need complex for selecting inner text
    // because not selecting whole text
    // can be caused by ::after transform
    public onFocusInput1(event) {
        const trg = event.target;
        if (!this.inputFocus1) {
            trg.focus();
            trg.select();
        }
        this.inputFocus1 = true;
    }

    public onFocusInput2(event) {
        const trg = event.target;
        if (!this.inputFocus2) {
            trg.focus();
            trg.select();
        }
        this.inputFocus2 = true;
    }

    public onFocusInput3(event) {
        const trg = event.target;
        if (!this.inputFocus3) {
            trg.focus();
            trg.select();
        }
        this.inputFocus3 = true;
    }

    public outOfFocus1() {
        this.inputFocus1 = false;
    }

    public outOfFocus2() {
        this.inputFocus2 = false;
    }

    public outOfFocus3() {
        this.inputFocus3 = false;
    }

    public parseVAT(vat: number): number {
        return vat ? vat * 100 : null;
    }

    public addNewFeature(): void {
        const id = this.changedAttributes.length;
        this.changedAttributes.push({ Value: '', AttributeId: this.allFeatures[0].id, Id: id});
    }

    public deleteFeature(id: number): void {
        this.changedAttributes = this.changedAttributes.filter(feature => feature.Id !== id);
    }

    public isFeatureDuplicated(id: number): boolean {
        return this.changedAttributes.filter(feature => id === feature.AttributeId).length > 1;
    }

    changeFeature(attribute: AttributeWithId) {
        const featureType = this.getFeatureType(attribute.AttributeId);
        if (featureType === FeatureTypes.Boolean) {
            attribute.Value = true;
        } else {
            attribute.Value = '';
        }
    }

    public goBack() {
        this._location.back();
    }

    public navigationBack(): void {
        this.generalService.navigateTo(this.navigationBackArray);
    }
}
